import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, TableShell, EmptyRow } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const empty = { name: "", phone: "", email: "", notes: "", vehicles: [] };
const emptyVehicle = { vehicle: "", plate: "", year: "", color: "", vin: "" };

export default function Customers() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/customers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/customers/${id}`); toast.success(t("deleted")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...empty, ...r, vehicles: (r.vehicles || []).map((v) => ({ ...v })) }); setEditId(r.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const addVehicle = () => setForm({ ...form, vehicles: [...form.vehicles, { ...emptyVehicle }] });
  const setVehicle = (idx, key, val) =>
    setForm({ ...form, vehicles: form.vehicles.map((v, i) => (i === idx ? { ...v, [key]: val } : v)) });
  const removeVehicle = (idx) => setForm({ ...form, vehicles: form.vehicles.filter((_, i) => i !== idx) });

  const filtered = rows.filter((r) =>
    [r.name, r.phone, ...(r.vehicles || []).flatMap((v) => [v.plate, v.vehicle, v.vin])]
      .join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader title={t("customers")}>
        <Button data-testid="add-customer-button" onClick={openNew} className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
          <Plus size={18} className="mr-2" /> {t("addCustomer")}
        </Button>
      </PageHeader>
      <Input data-testid="customer-search-input" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)}
        className="max-w-sm mb-4 h-11 bg-white" />
      <TableShell headers={[t("name"), t("phone"), t("email"), t("vehicles"), t("actions")]}>
        {filtered.length === 0 && <EmptyRow colSpan={5} />}
        {filtered.map((r) => (
          <tr key={r.id} data-testid={`customer-row-${r.id}`} className="hover:bg-slate-50">
            <td className="px-4 py-3">
              <Link to={`/customers/${r.id}`} data-testid={`customer-profile-link-${r.id}`}
                className="font-semibold text-slate-900 hover:text-sky-600 hover:underline">
                {r.name}
              </Link>
            </td>
            <td className="px-4 py-3">{r.phone}</td>
            <td className="px-4 py-3">{r.email}</td>
            <td className="px-4 py-3">
              {(r.vehicles || []).length === 0 && <span className="text-slate-300">—</span>}
              {(r.vehicles || []).map((v) => (
                <div key={v.id} className="text-sm">
                  {v.vehicle} {v.year && <span className="text-slate-400">{v.year}</span>}
                  {v.plate && <span className="uppercase font-medium text-slate-600"> · {v.plate}</span>}
                </div>
              ))}
            </td>
            <td className="px-4 py-3">
              <div className="flex gap-1">
                <Button data-testid={`edit-customer-${r.id}`} variant="ghost" size="icon" onClick={() => openEdit(r)}>
                  <Pencil size={15} />
                </Button>
                <Button data-testid={`delete-customer-${r.id}`} variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => remove(r.id)}>
                  <Trash2 size={15} />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </TableShell>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? t("editCustomer") : t("addCustomer")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5">
                <Label>{t("name")} *</Label>
                <Input data-testid="customer-name-input" required value={form.name} onChange={set("name")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("phone")}</Label>
                <Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} className="h-11" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("email")}</Label>
                <Input data-testid="customer-email-input" type="email" value={form.email} onChange={set("email")} className="h-11" />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>{t("notes")}</Label>
                <Input data-testid="customer-notes-input" value={form.notes} onChange={set("notes")} className="h-11" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t("vehicles")}</Label>
                <Button data-testid="add-vehicle-button" type="button" variant="outline" size="sm" className="h-9" onClick={addVehicle}>
                  <Plus size={14} className="mr-1" /> {t("addVehicle")}
                </Button>
              </div>
              {form.vehicles.map((v, idx) => (
                <div key={idx} className="border border-slate-200 rounded-lg p-3 relative bg-slate-50/50">
                  <button type="button" data-testid={`remove-vehicle-${idx}`} onClick={() => removeVehicle(idx)}
                    className="absolute top-2 right-2 text-red-400 hover:text-red-600 p-1">
                    <X size={15} />
                  </button>
                  <div className="grid grid-cols-2 gap-3 pr-6">
                    <Input data-testid={`vehicle-model-${idx}`} placeholder={`${t("vehicle")} (Honda Civic)`} value={v.vehicle}
                      onChange={(e) => setVehicle(idx, "vehicle", e.target.value)} className="h-10" />
                    <Input data-testid={`vehicle-color-${idx}`} placeholder={t("color")} value={v.color}
                      onChange={(e) => setVehicle(idx, "color", e.target.value)} className="h-10" />
                    <Input data-testid={`vehicle-plate-${idx}`} placeholder={t("plate")} value={v.plate}
                      onChange={(e) => setVehicle(idx, "plate", e.target.value)} className="h-10" />
                    <Input data-testid={`vehicle-year-${idx}`} placeholder={t("year")} value={v.year}
                      onChange={(e) => setVehicle(idx, "year", e.target.value)} className="h-10" />
                    <Input data-testid={`vehicle-vin-${idx}`} placeholder={`${t("vin")} (1HGBH41JXMN109186)`} value={v.vin}
                      onChange={(e) => setVehicle(idx, "vin", e.target.value)} className="h-10 col-span-2" />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="customer-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
